import { useState, useRef } from 'react'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { Save, RotateCcw, Key, Eye, EyeOff, CheckCircle, XCircle, AlertCircle, Trash2, Upload, Image as ImageIcon, FileImage, Volume2, VolumeX, Bot } from 'lucide-react'
import { api, ApiProvider, ApiKeyStatus, KAIJU_IMAGE_SPECS, AgentModeFeature } from '../lib/api'
import { KaijuLoader } from '../components/KaijuLoader'
import KgObservabilityPanel from '../components/settings/KgObservabilityPanel'
import { NotebookLmAuthSection } from '../components/settings/NotebookLmAuthSection'
import { getSoundEnabled, setSoundEnabled, notify } from '../lib/notify'

const API_PROVIDERS: { id: ApiProvider; name: string; description: string }[] = [
  { id: 'anthropic', name: 'Anthropic', description: 'Claude AI models for analysis and generation' },
  { id: 'openai', name: 'OpenAI', description: 'GPT models and embeddings' },
  { id: 'google', name: 'Google AI', description: 'Gemini models for AI features' },
  { id: 'perplexity', name: 'Perplexity', description: 'Research and web search AI' },
  { id: 'fireflies', name: 'Fireflies.ai', description: 'Meeting transcription and intelligence' },
  { id: 'mistral', name: 'Mistral AI', description: 'Voxtral audio transcription' },
]

export function SettingsPage() {
  return (
    <div className="space-y-6">
      <div>
        <h2 className="text-2xl font-bold text-gray-900 dark:text-white">Settings</h2>
        <p className="mt-1 text-sm text-gray-500 dark:text-gray-400">
          Configure API keys, scoring weights, and preferences
        </p>
      </div>

      {/* API Keys Section */}
      <ApiKeysSection />

      {/* NotebookLM Authentication */}
      <NotebookLmAuthSection />

      {/* Scoring Weights */}
      <ScoringWeightsSection />

      {/* Kaiju Images */}
      <KaijuImagesSection />

      {/* Notification Preferences */}
      <NotificationPreferencesSection />

      {/* Agent Mode */}
      <AgentModeSection />

      {/* Knowledge Graph Observability */}
      <div className="glass-card">
        <KgObservabilityPanel />
      </div>

    </div>
  )
}

function ApiKeysSection() {
  const queryClient = useQueryClient()

  const { data: settings, isLoading, error } = useQuery({
    queryKey: ['settings', 'api-keys'],
    queryFn: () => api.settings.getApiKeys(),
  })

  if (isLoading) {
    return (
      <div className="glass-card">
        <div className="flex items-center gap-3 mb-6">
          <Key className="w-5 h-5 text-gray-600 dark:text-gray-400" />
          <h3 className="text-lg font-semibold text-gray-900 dark:text-white">API Keys</h3>
        </div>
        <div className="flex items-center justify-center py-8">
          <KaijuLoader size="md" />
          <span className="ml-2 text-gray-500 dark:text-gray-400">Loading settings...</span>
        </div>
      </div>
    )
  }

  if (error) {
    return (
      <div className="glass-card">
        <div className="flex items-center gap-3 mb-6">
          <Key className="w-5 h-5 text-gray-600 dark:text-gray-400" />
          <h3 className="text-lg font-semibold text-gray-900 dark:text-white">API Keys</h3>
        </div>
        <div className="bg-red-100/80 dark:bg-red-500/20 border border-red-300/50 dark:border-red-500/30 rounded-lg p-4 backdrop-blur-sm">
          <p className="text-sm text-red-800 dark:text-red-200">
            Failed to load API key settings. Please try refreshing the page.
          </p>
        </div>
      </div>
    )
  }

  const getKeyStatus = (provider: ApiProvider): ApiKeyStatus | undefined => {
    return settings?.keys.find(k => k.provider === provider)
  }

  return (
    <div className="glass-card">
      <div className="flex items-center gap-3 mb-2">
        <Key className="w-5 h-5 text-gray-600 dark:text-gray-400" />
        <h3 className="text-lg font-semibold text-gray-900 dark:text-white">API Keys</h3>
      </div>
      <p className="text-sm text-gray-500 dark:text-gray-400 mb-6">
        Configure API keys for AI providers. You can use environment-configured keys or override with your own.
      </p>

      <div className="space-y-4">
        {API_PROVIDERS.map(provider => (
          <ApiKeyCard
            key={provider.id}
            provider={provider}
            status={getKeyStatus(provider.id)}
            onUpdate={() => queryClient.invalidateQueries({ queryKey: ['settings', 'api-keys'] })}
          />
        ))}
      </div>
    </div>
  )
}

interface ApiKeyCardProps {
  provider: { id: ApiProvider; name: string; description: string }
  status?: ApiKeyStatus
  onUpdate: () => void
}

function ApiKeyCard({ provider, status, onUpdate }: ApiKeyCardProps) {
  const [isEditing, setIsEditing] = useState(false)
  const [apiKey, setApiKey] = useState('')
  const [showKey, setShowKey] = useState(false)
  const [validationResult, setValidationResult] = useState<{ valid: boolean; error?: string } | null>(null)
  const [isValidating, setIsValidating] = useState(false)

  const updateMutation = useMutation({
    mutationFn: ({ apiKey, useCustom }: { apiKey: string | null; useCustom: boolean }) =>
      api.settings.updateApiKey(provider.id, apiKey, useCustom),
    onSuccess: () => {
      onUpdate()
      setIsEditing(false)
      setApiKey('')
      setValidationResult(null)
    },
  })

  const deleteMutation = useMutation({
    mutationFn: () => api.settings.deleteApiKey(provider.id),
    onSuccess: () => {
      onUpdate()
      setIsEditing(false)
      setApiKey('')
      setValidationResult(null)
    },
  })

  const handleValidate = async () => {
    if (!apiKey.trim()) return
    setIsValidating(true)
    setValidationResult(null)
    try {
      const result = await api.settings.validateApiKey(provider.id, apiKey)
      setValidationResult(result)
    } catch (err) {
      setValidationResult({ valid: false, error: 'Validation failed' })
    } finally {
      setIsValidating(false)
    }
  }

  const handleSave = () => {
    if (!apiKey.trim()) return
    updateMutation.mutate({ apiKey, useCustom: true })
  }

  const handleToggleUseCustom = () => {
    if (!status?.hasCustomKey) return
    updateMutation.mutate({ apiKey: null, useCustom: !status.useCustom })
  }

  const handleDelete = () => {
    if (confirm(`Are you sure you want to delete your custom ${provider.name} API key?`)) {
      deleteMutation.mutate()
    }
  }

  const getStatusIndicator = () => {
    if (!status) {
      return (
        <div className="flex items-center gap-1.5 text-gray-500 dark:text-gray-400">
          <AlertCircle className="w-4 h-4" />
          <span className="text-xs">Not configured</span>
        </div>
      )
    }

    if (status.source === 'custom') {
      return (
        <div className="flex items-center gap-1.5 text-green-600 dark:text-green-400">
          <CheckCircle className="w-4 h-4" />
          <span className="text-xs">Custom key active</span>
        </div>
      )
    }

    if (status.source === 'environment') {
      return (
        <div className="flex items-center gap-1.5 text-blue-600 dark:text-blue-400">
          <CheckCircle className="w-4 h-4" />
          <span className="text-xs">Using environment key</span>
        </div>
      )
    }

    return (
      <div className="flex items-center gap-1.5 text-amber-600 dark:text-amber-400">
        <XCircle className="w-4 h-4" />
        <span className="text-xs">No key available</span>
      </div>
    )
  }

  return (
    <div className="border border-gray-200/50 dark:border-white/10 rounded-lg p-4 bg-gray-50/50 dark:bg-white/5 backdrop-blur-sm">
      <div className="flex items-start justify-between">
        <div className="flex-1">
          <div className="flex items-center gap-3">
            <h4 className="font-medium text-gray-900 dark:text-white">{provider.name}</h4>
            {getStatusIndicator()}
          </div>
          <p className="text-sm text-gray-500 dark:text-gray-400 mt-1">{provider.description}</p>
        </div>

        {!isEditing && (
          <div className="flex items-center gap-2">
            {status?.hasCustomKey && (
              <>
                <label className="flex items-center gap-2 text-sm text-gray-700 dark:text-gray-300">
                  <input
                    type="checkbox"
                    checked={status.useCustom}
                    onChange={handleToggleUseCustom}
                    disabled={updateMutation.isPending}
                    className="rounded border-gray-300 dark:border-gray-600 text-primary-600 focus:ring-primary-500 bg-white dark:bg-surface-800"
                  />
                  Use custom
                </label>
                <button
                  onClick={handleDelete}
                  disabled={deleteMutation.isPending}
                  className="p-1.5 text-red-600 dark:text-red-400 hover:bg-red-50 dark:hover:bg-red-500/20 rounded"
                  title="Delete custom key"
                >
                  <Trash2 className="w-4 h-4" />
                </button>
              </>
            )}
            <button
              onClick={() => setIsEditing(true)}
              className="btn-secondary text-sm py-1.5 px-3"
            >
              {status?.hasCustomKey ? 'Update Key' : 'Add Key'}
            </button>
          </div>
        )}
      </div>

      {isEditing && (
        <div className="mt-4 pt-4 border-t border-gray-200/50 dark:border-white/10">
          <div className="flex gap-2">
            <div className="flex-1 relative">
              <input
                type={showKey ? 'text' : 'password'}
                value={apiKey}
                onChange={(e) => {
                  setApiKey(e.target.value)
                  setValidationResult(null)
                }}
                placeholder={`Enter your ${provider.name} API key`}
                className="input pr-10 w-full"
                autoComplete="off"
              />
              <button
                type="button"
                onClick={() => setShowKey(!showKey)}
                className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600 dark:hover:text-gray-300"
              >
                {showKey ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
              </button>
            </div>
            <button
              onClick={handleValidate}
              disabled={!apiKey.trim() || isValidating}
              className="btn-secondary text-sm py-1.5 px-3 flex items-center gap-1.5"
            >
              {isValidating ? (
                <KaijuLoader size="sm" />
              ) : (
                'Validate'
              )}
            </button>
          </div>

          {validationResult && (
            <div className={`mt-2 p-2 rounded text-sm backdrop-blur-sm ${validationResult.valid ? 'bg-green-100/80 dark:bg-green-500/20 text-green-700 dark:text-green-300' : 'bg-red-100/80 dark:bg-red-500/20 text-red-700 dark:text-red-300'}`}>
              {validationResult.valid ? (
                <span className="flex items-center gap-1.5">
                  <CheckCircle className="w-4 h-4" />
                  API key is valid
                </span>
              ) : (
                <span className="flex items-center gap-1.5">
                  <XCircle className="w-4 h-4" />
                  {validationResult.error || 'Invalid API key'}
                </span>
              )}
            </div>
          )}

          <div className="flex justify-end gap-2 mt-3">
            <button
              onClick={() => {
                setIsEditing(false)
                setApiKey('')
                setValidationResult(null)
              }}
              className="btn-secondary text-sm py-1.5 px-3"
            >
              Cancel
            </button>
            <button
              onClick={handleSave}
              disabled={!apiKey.trim() || updateMutation.isPending}
              className="btn-primary text-sm py-1.5 px-3 flex items-center gap-1.5"
            >
              {updateMutation.isPending ? (
                <KaijuLoader size="sm" />
              ) : (
                <Save className="w-4 h-4" />
              )}
              Save Key
            </button>
          </div>
        </div>
      )}

      {status?.hasCustomKey && status.lastUpdated && (
        <div className="mt-2 text-xs text-gray-400 dark:text-gray-500">
          Custom key last updated: {new Date(status.lastUpdated).toLocaleDateString()}
        </div>
      )}
    </div>
  )
}

function ScoringWeightsSection() {
  return (
    <div className="glass-card">
      <div className="flex items-center justify-between mb-6">
        <h3 className="text-lg font-semibold text-gray-900 dark:text-white">Scoring Weights</h3>
        <div className="flex gap-2">
          <button className="btn-secondary flex items-center gap-2">
            <RotateCcw className="w-4 h-4" />
            Reset to Defaults
          </button>
          <button className="btn-primary flex items-center gap-2">
            <Save className="w-4 h-4" />
            Save Changes
          </button>
        </div>
      </div>

      {/* Title Keywords */}
      <div className="mb-8">
        <h4 className="font-medium text-gray-900 dark:text-white mb-4">Title Keywords</h4>
        <p className="text-sm text-gray-500 dark:text-gray-400 mb-4">
          Points awarded when a lead's title contains these keywords
        </p>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          <WeightInput label="DevOps" defaultValue={15} />
          <WeightInput label="Platform" defaultValue={12} />
          <WeightInput label="Security" defaultValue={12} />
          <WeightInput label="Infrastructure" defaultValue={10} />
          <WeightInput label="Cloud" defaultValue={10} />
          <WeightInput label="CTO" defaultValue={15} />
          <WeightInput label="CISO" defaultValue={15} />
          <WeightInput label="VP" defaultValue={10} />
          <WeightInput label="Director" defaultValue={8} />
          <WeightInput label="Manager" defaultValue={5} />
          <WeightInput label="Architect" defaultValue={10} />
          <WeightInput label="Engineer" defaultValue={8} />
        </div>
      </div>

      {/* Organization Type */}
      <div className="mb-8">
        <h4 className="font-medium text-gray-900 dark:text-white mb-4">Organization Type</h4>
        <p className="text-sm text-gray-500 dark:text-gray-400 mb-4">
          Points based on the type of public sector organization
        </p>
        <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
          <WeightInput label="University" defaultValue={20} />
          <WeightInput label="Federal" defaultValue={18} />
          <WeightInput label="State" defaultValue={15} />
          <WeightInput label="Local" defaultValue={10} />
          <WeightInput label="Other" defaultValue={5} />
        </div>
      </div>

      {/* State Priority */}
      <div className="mb-8">
        <h4 className="font-medium text-gray-900 dark:text-white mb-4">State Priority</h4>
        <p className="text-sm text-gray-500 dark:text-gray-400 mb-4">
          Points based on the lead's state (higher for priority markets)
        </p>
        <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
          <WeightInput label="CA" defaultValue={10} />
          <WeightInput label="TX" defaultValue={10} />
          <WeightInput label="NY" defaultValue={8} />
          <WeightInput label="FL" defaultValue={8} />
          <WeightInput label="IL" defaultValue={7} />
          <WeightInput label="PA" defaultValue={7} />
          <WeightInput label="OH" defaultValue={6} />
          <WeightInput label="GA" defaultValue={6} />
          <WeightInput label="NC" defaultValue={6} />
          <WeightInput label="MI" defaultValue={5} />
        </div>
      </div>

      {/* Source Quality */}
      <div>
        <h4 className="font-medium text-gray-900 dark:text-white mb-4">Source Quality</h4>
        <p className="text-sm text-gray-500 dark:text-gray-400 mb-4">
          Points based on how the lead was acquired
        </p>
        <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
          <WeightInput label="Trial Signup" defaultValue={25} />
          <WeightInput label="Demo Request" defaultValue={20} />
          <WeightInput label="Referral" defaultValue={18} />
          <WeightInput label="Event" defaultValue={15} />
          <WeightInput label="Webinar" defaultValue={10} />
          <WeightInput label="Content Download" defaultValue={5} />
        </div>
      </div>
    </div>
  )
}

interface WeightInputProps {
  label: string
  defaultValue: number
}

function WeightInput({ label, defaultValue }: WeightInputProps) {
  const [value, setValue] = useState(defaultValue)

  return (
    <div>
      <label className="block text-sm text-gray-600 dark:text-gray-400 mb-1">{label}</label>
      <input
        type="number"
        min={0}
        max={100}
        value={value}
        onChange={(e) => setValue(parseInt(e.target.value) || 0)}
        className="input text-center"
      />
    </div>
  )
}

function KaijuImagesSection() {
  const queryClient = useQueryClient()
  const fileInputRef = useRef<HTMLInputElement>(null)
  const [uploadError, setUploadError] = useState<string | null>(null)

  const { data: imagesData, isLoading, error } = useQuery({
    queryKey: ['settings', 'kaiju-images'],
    queryFn: () => api.settings.getKaijuImages(),
  })

  const uploadMutation = useMutation({
    mutationFn: (file: File) => api.settings.uploadKaijuImage(file),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['settings', 'kaiju-images'] })
      setUploadError(null)
      if (fileInputRef.current) {
        fileInputRef.current.value = ''
      }
    },
    onError: (error: Error) => {
      setUploadError(error.message)
    },
  })

  const deleteMutation = useMutation({
    mutationFn: (filename: string) => api.settings.deleteKaijuImage(filename),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['settings', 'kaiju-images'] })
    },
  })

  const handleFileSelect = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0]
    if (!file) return

    // Validate file type
    if (!KAIJU_IMAGE_SPECS.allowedFormats.includes(file.type as any)) {
      setUploadError(`Invalid file type. Allowed: ${KAIJU_IMAGE_SPECS.allowedFormats.join(', ')}`)
      return
    }

    // Validate file size
    if (file.size > KAIJU_IMAGE_SPECS.maxFileSize) {
      setUploadError(
        `File too large. Maximum: ${KAIJU_IMAGE_SPECS.maxFileSize / 1024}KB. Your file: ${Math.round(file.size / 1024)}KB`
      )
      return
    }

    uploadMutation.mutate(file)
  }

  const handleDelete = (filename: string) => {
    if (confirm(`Are you sure you want to delete ${filename}?`)) {
      deleteMutation.mutate(filename)
    }
  }

  const formatFileSize = (bytes: number) => {
    if (bytes < 1024) return `${bytes} B`
    if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`
    return `${(bytes / (1024 * 1024)).toFixed(1)} MB`
  }

  return (
    <div className="glass-card">
      <div className="flex items-center gap-3 mb-2">
        <FileImage className="w-5 h-5 text-gray-600 dark:text-gray-400" />
        <h3 className="text-lg font-semibold text-gray-900 dark:text-white">Kaiju Images</h3>
      </div>
      <p className="text-sm text-gray-500 dark:text-gray-400 mb-6">
        Manage custom kaiju images for the AI generation progress modal. Upload pixel art or generated images.
      </p>

      {/* Image Specifications */}
      <div className="bg-blue-100/80 dark:bg-blue-500/20 border border-blue-300/50 dark:border-blue-500/30 rounded-lg p-4 backdrop-blur-sm mb-6">
        <h4 className="font-medium text-blue-900 dark:text-blue-200 mb-2">Image Specifications</h4>
        <div className="grid grid-cols-2 gap-x-6 gap-y-2 text-sm text-blue-800 dark:text-blue-300">
          <div><strong>Format:</strong> JPG or PNG</div>
          <div><strong>Max Size:</strong> 500KB</div>
          <div><strong>Minimum:</strong> {KAIJU_IMAGE_SPECS.minWidth} × {KAIJU_IMAGE_SPECS.minHeight}</div>
          <div><strong>Ideal:</strong> {KAIJU_IMAGE_SPECS.idealWidth} × {KAIJU_IMAGE_SPECS.idealHeight}</div>
          <div><strong>Maximum:</strong> {KAIJU_IMAGE_SPECS.maxWidth} × {KAIJU_IMAGE_SPECS.maxHeight}</div>
          <div><strong>Aspect Ratio:</strong> 16:9 (landscape)</div>
        </div>
      </div>

      {/* Upload Section */}
      <div className="mb-6">
        <input
          ref={fileInputRef}
          type="file"
          accept=".jpg,.jpeg,.png"
          onChange={handleFileSelect}
          className="hidden"
        />
        <button
          onClick={() => fileInputRef.current?.click()}
          disabled={uploadMutation.isPending}
          className="btn-primary flex items-center gap-2"
        >
          {uploadMutation.isPending ? (
            <KaijuLoader size="sm" />
          ) : (
            <Upload className="w-4 h-4" />
          )}
          Upload New Image
        </button>

        {uploadError && (
          <div className="mt-2 p-2 rounded text-sm bg-red-100/80 dark:bg-red-500/20 text-red-700 dark:text-red-300 backdrop-blur-sm">
            {uploadError}
          </div>
        )}

        {uploadMutation.isSuccess && !uploadError && (
          <div className="mt-2 p-2 rounded text-sm bg-green-100/80 dark:bg-green-500/20 text-green-700 dark:text-green-300 backdrop-blur-sm">
            Image uploaded successfully!
          </div>
        )}
      </div>

      {/* Images Grid */}
      {isLoading ? (
        <div className="flex items-center justify-center py-8">
          <KaijuLoader size="md" />
          <span className="ml-2 text-gray-500 dark:text-gray-400">Loading images...</span>
        </div>
      ) : error ? (
        <div className="bg-red-100/80 dark:bg-red-500/20 border border-red-300/50 dark:border-red-500/30 rounded-lg p-4 backdrop-blur-sm">
          <p className="text-sm text-red-800 dark:text-red-200">
            Failed to load kaiju images. Please try refreshing the page.
          </p>
        </div>
      ) : imagesData?.images.length === 0 ? (
        <div className="text-center py-8 text-gray-500 dark:text-gray-400">
          <ImageIcon className="w-12 h-12 mx-auto mb-2 opacity-50" />
          <p>No kaiju images uploaded yet.</p>
          <p className="text-sm mt-1">Upload images to customize your AI generation modal.</p>
        </div>
      ) : (
        <div>
          <h4 className="font-medium text-gray-900 dark:text-white mb-3">
            Uploaded Images ({imagesData?.images.length || 0})
          </h4>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {imagesData?.images.map((image) => (
              <div
                key={image.filename}
                className="border border-gray-200/50 dark:border-white/10 rounded-lg p-3 bg-gray-50/50 dark:bg-white/5 backdrop-blur-sm"
              >
                <div className="aspect-video rounded overflow-hidden mb-2 bg-gray-900">
                  <img
                    src={image.url}
                    alt={image.filename}
                    className="w-full h-full object-cover"
                    style={{ imageRendering: 'pixelated' }}
                  />
                </div>
                <div className="space-y-1">
                  <p className="text-xs font-mono text-gray-600 dark:text-gray-400 truncate" title={image.filename}>
                    {image.filename}
                  </p>
                  <div className="flex items-center justify-between text-xs text-gray-500 dark:text-gray-400">
                    <span>{formatFileSize(image.size)}</span>
                    <button
                      onClick={() => handleDelete(image.filename)}
                      disabled={deleteMutation.isPending}
                      className="p-1 text-red-600 dark:text-red-400 hover:bg-red-50 dark:hover:bg-red-500/20 rounded"
                      title="Delete image"
                    >
                      <Trash2 className="w-3.5 h-3.5" />
                    </button>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  )
}

function NotificationPreferencesSection() {
  const [soundOn, setSoundOn] = useState(getSoundEnabled)

  const handleToggle = () => {
    const next = !soundOn
    setSoundOn(next)
    setSoundEnabled(next)
    if (next) {
      notify.sfx.click()
    }
  }

  return (
    <div className="glass-card">
      <div className="flex items-center gap-3 mb-2">
        {soundOn ? (
          <Volume2 className="w-5 h-5 text-lime-500" />
        ) : (
          <VolumeX className="w-5 h-5 text-gray-400" />
        )}
        <h3 className="text-lg font-semibold text-gray-900 dark:text-white">Notification Preferences</h3>
      </div>
      <p className="text-sm text-gray-500 dark:text-gray-400 mb-6">
        Configure notification sounds and toast behavior.
      </p>

      <div className="border border-gray-200/50 dark:border-white/10 rounded-lg p-4 bg-gray-50/50 dark:bg-white/5 backdrop-blur-sm">
        <div className="flex items-center justify-between">
          <div>
            <h4 className="font-medium text-gray-900 dark:text-white">Play notification sounds</h4>
            <p className="text-sm text-gray-500 dark:text-gray-400 mt-0.5">
              Kaiju-themed audio feedback for actions and AI generation
            </p>
          </div>
          <button
            onClick={handleToggle}
            className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors ${
              soundOn ? 'bg-lime-500' : 'bg-gray-600'
            }`}
          >
            <span
              className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${
                soundOn ? 'translate-x-6' : 'translate-x-1'
              }`}
            />
          </button>
        </div>
      </div>
    </div>
  )
}

const AGENT_MODE_TOGGLES: { feature: AgentModeFeature; label: string; description: string }[] = [
  { feature: 'chat', label: 'AI Chat', description: 'Use Mastra agent with autonomous tool orchestration instead of fixed pipeline' },
  { feature: 'council', label: 'LLM Council', description: 'Agent-orchestrated 3-round debate instead of sequential pipeline' },
  { feature: 'kgContext', label: 'KG Context Builder', description: 'Adaptive Neo4j queries via agent instead of fixed scope dispatch' },
  { feature: 'briefing', label: 'Executive Briefing', description: 'Agent-driven artifact assessment and report generation' },
]

function AgentModeSection() {
  const queryClient = useQueryClient()

  const { data, isLoading } = useQuery({
    queryKey: ['settings', 'agent-mode'],
    queryFn: () => api.settings.getAgentMode(),
  })

  const toggleMutation = useMutation({
    mutationFn: ({ feature, enabled }: { feature: AgentModeFeature; enabled: boolean }) =>
      api.settings.updateAgentMode(feature, enabled),
    onSuccess: (updatedSettings) => {
      queryClient.setQueryData(['settings', 'agent-mode'], updatedSettings)
    },
    onError: (err) => {
      notify.error('Failed to update agent mode', { description: err instanceof Error ? err.message : String(err) })
    },
  })

  const handleToggle = (feature: AgentModeFeature) => {
    const current = data?.[feature] ?? false
    toggleMutation.mutate({ feature, enabled: !current })
  }

  return (
    <div className="glass-card">
      <div className="flex items-center gap-3 mb-2">
        <Bot className="w-5 h-5 text-purple-500" />
        <h3 className="text-lg font-semibold text-gray-900 dark:text-white">Agent Mode</h3>
        <span className="text-xs px-2 py-0.5 rounded-full bg-purple-100 dark:bg-purple-500/20 text-purple-700 dark:text-purple-300 font-medium">
          Experimental
        </span>
      </div>
      <p className="text-sm text-gray-500 dark:text-gray-400 mb-6">
        Switch individual AI features between deterministic pipeline mode (default) and autonomous Mastra agent mode.
        Agent mode enables adaptive tool use and multi-step reasoning but may produce less predictable results.
      </p>

      <div className="space-y-3">
        {AGENT_MODE_TOGGLES.map(({ feature, label, description }) => {
          const enabled = data?.[feature] ?? false
          return (
            <div
              key={feature}
              className="border border-gray-200/50 dark:border-white/10 rounded-lg p-4 bg-gray-50/50 dark:bg-white/5 backdrop-blur-sm"
            >
              <div className="flex items-center justify-between">
                <div>
                  <h4 className="font-medium text-gray-900 dark:text-white">{label}</h4>
                  <p className="text-sm text-gray-500 dark:text-gray-400 mt-0.5">{description}</p>
                </div>
                <button
                  onClick={() => handleToggle(feature)}
                  disabled={isLoading || toggleMutation.isPending}
                  className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors ${
                    enabled ? 'bg-purple-500' : 'bg-gray-600'
                  } ${isLoading || toggleMutation.isPending ? 'opacity-50 cursor-not-allowed' : ''}`}
                >
                  <span
                    className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${
                      enabled ? 'translate-x-6' : 'translate-x-1'
                    }`}
                  />
                </button>
              </div>
            </div>
          )
        })}
      </div>
    </div>
  )
}

